import React, {useEffect, useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import OfficesSelect from "../../form/OfficesSelect";
import FormSection from "../../form/Section";
import FormRepeater from "../../form/FormRepeater";
import CardRow from "./CardRow";
import InfoChip from "../../InfoChip";
import Amount from "../../Amount";
import MessageError from "../../MessageError";
import CorporateCustomersSelect from "../../form/CorporateCustomersSelect";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import { ENABLE_OXFORD_SELL_ORDER_LIMIT, SELL_ORDER_LIMIT_AMOUNT } from "../../../config/variables";
import SpecialtyItemsSection from "../../sell-specialty-items/SpecialtyItemsSection";
import { specialtyItemCount } from "../../sell-specialty-items/specialtyItemTotals";
import { GetSellSpecialtyItems } from "../../../api/sell-specialty-items/getSellSpecialtyItems";
import { MOD_SELL_SPECIALTY_ITEMS } from "../../../contstants";

// A company may have at most 100 active items on one page of the catalog, which
// is the largest page bps-go serves and far more than the 20 a single order can
// carry.
const CATALOG_PAGE_SIZE = 100;

const formatSellOrderLimit = (language) => new Intl.NumberFormat(
    language?.startsWith('fr') ? 'fr-CA' : 'en-CA',
    { style: 'currency', currency: 'CAD', minimumFractionDigits: 0, maximumFractionDigits: 0 },
).format(SELL_ORDER_LIMIT_AMOUNT);

const AddCard = ({ formData, setFormData, prnError }) => {
    const { t, i18n } = useTranslation();
    const { configs, allowedModules } = useGlobalContext();
    const enableSellOrderLimit = configs[ENABLE_OXFORD_SELL_ORDER_LIMIT] || false;
    const memoizedRowData = useMemo(() => formData.cards, [formData.cards]);

    // Gated on the module, not on a permission: sell order access already
    // carries the catalog read, so an agent who may raise an order may pick
    // items on it.
    const specialtyItemsEnabled = allowedModules?.includes(MOD_SELL_SPECIALTY_ITEMS) || false;
    const [catalog, setCatalog] = useState([]);
    // Starts true so the picker says it is loading rather than briefly claiming
    // the company has no items.
    const [catalogLoading, setCatalogLoading] = useState(true);

    useEffect(() => {
        if (!specialtyItemsEnabled) {
            setCatalogLoading(false);
            return;
        }

        const fetchCatalog = async () => {
            setCatalogLoading(true);
            try {
                const response = await GetSellSpecialtyItems({
                    page: 1,
                    perPage: CATALOG_PAGE_SIZE,
                    isActive: true,
                });
                setCatalog(response?.data || []);
            } catch (err) {
                console.log("Failed to load specialty items: " + err);
            } finally {
                setCatalogLoading(false);
            }
        };

        fetchCatalog();
    }, [specialtyItemsEnabled]);
    return (
        <div className="step">
            <div className="container container-md">
                <div className="row mb-2">
                    <div className="col-xs-12 col-md-6">
                        <FormSection title={t("Select an Office")} required>
                            <OfficesSelect
                                onChange={officeId => setFormData(prevData => ({
                                    ...prevData,
                                    office_id: officeId
                                }))}
                                setProvinceCode={provinceCode => setFormData(prevData => ({
                                    ...prevData,
                                    province_code: provinceCode
                                }))}
                                defaultOfficeIds={[formData.office_id]}
                            />
                        </FormSection>
                    </div>
                    <div className="col-xs-12 col-md-6">
                        <FormSection title={t("Client Company Name")}>
                            <CorporateCustomersSelect
                                onChange={customer => setFormData(prevData => ({
                                    ...prevData,
                                    corporate_customer_id: customer.value,
                                    client_company_name: customer.label
                                }))}
                                defaultCustomerIds={[formData.corporate_customer_id]}
                            />
                        </FormSection>
                    </div>
                </div>

                <div className="mb-3">
                    <h5 className="mb-0">{t("Card")}</h5>
                    <hr className="size-3"/>
                    <FormRepeater
                        columns={[
                            t("Proxy Number"),
                            "",
                            t("Card Type"),
                            t("Amount"),
                            t("Action"),
                        ]}
                        maxRows={100}
                        addRowText={t("Add Card")}
                        RenderComponent={CardRow}
                        onRowChange={cards => setFormData(prevData => ({
                            ...prevData,
                            cards
                        }))}
                        rowData={memoizedRowData}
                        formData={formData}
                        setFormData={setFormData}
                    />
                </div>
                {prnError && (
                    <MessageError error={prnError} className="text-left"/>
                )}
                {specialtyItemsEnabled && (
                    <SpecialtyItemsSection
                        lines={formData.specialty_items}
                        onChange={specialtyItems => setFormData(prevData => ({
                            ...prevData,
                            specialty_items: specialtyItems
                        }))}
                        catalog={catalog}
                        taxRate={formData.provisional_tax_rate}
                        loading={catalogLoading}
                    />
                )}
                {enableSellOrderLimit && <div className="text-right mb-2">
                    <MessageError error={t('sell_order_limit', { amount: formatSellOrderLimit(i18n.language) })} isHtml className="text-left fw-500"/>
                </div>}
                <div className="text-right">
                    <InfoChip value={formData.cards.length} description={t('Cards')} className="mr-3"/>
                    {specialtyItemsEnabled && (
                        <InfoChip
                            value={specialtyItemCount(formData.specialty_items)}
                            description={t('Specialty Items')}
                            className="mr-3"
                        />
                    )}
                    <InfoChip value={<Amount amount={formData.subtotal_cost} />} description={t('Subtotal')}/>
                </div>
            </div>
        </div>
    );
};

export default AddCard;